import {
  getHubUploads,
  getHubFileLink,
  deleteHubFile,
  registerUpload
} from '../library/aws'

import {
  bytesConvertor,
  timeConvertor
} from '../library/general'

export const UPLOADING = 'UPLOADING'
export const COMPLETE = 'COMPLETE'
export const FAILED = 'FAILED'
export const CANCELLED = 'CANCELLED'
export const QUEUED = 'QUEUED'

export const NO_VERSION = 'NO_VERSION'
export const DUPLICATE = 'DUPLICATE'

export const RECENT = 'recent'
export const LONG_TERM = 'longTerm'

// Long Term Storage Version filter values ('' = all Versions)
export const VERSION_FILTER_ALL = ''
export const VERSION_FILTER_UNASSIGNED = 'unassigned'

export const DEFAULT_PAGE_SIZE = 10
export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100]

export const LINK_MODE_DOWNLOAD = 'download'
export const LINK_MODE_PLAY = 'play'

const initialState = {
  fetched: false,
  loading: false,
  hasError: false,
  error: '',
  eligible: false,
  account: null,
  bucket: null,
  // Versions the user may upload against (Aberdeen Hub Upload checked); the picklist is required
  versions: [],
  selectedVersionId: '',
  recentUploads: [],
  recentPage: 1,
  recentPageSize: DEFAULT_PAGE_SIZE,
  // Long Term Storage section: only when the account has a Hub Version with Long-term Storage
  longTermEnabled: false,
  longTermFiles: [],
  longTermPage: 1,
  longTermPageSize: DEFAULT_PAGE_SIZE,
  longTermVersionFilter: VERSION_FILTER_ALL,
  uploads: {},
  rejected: [],
  // mode: LINK_MODE_DOWNLOAD shows the signed link; LINK_MODE_PLAY opens the proxy in the player
  fileLink: {
    show: false,
    loading: false,
    mode: 'download',
    file: null,
    fileId: null,
    fileName: '',
    linkType: '',
    link: null,
    hasError: false,
    error: ''
  },
  // Hard-delete confirmation for one long-term file
  fileDelete: {
    show: false,
    loading: false,
    file: null,
    hasError: false,
    error: ''
  }
}

const RESET_STATE = 'uploads/RESET_STATE'
const UPDATE = 'uploads/UPDATE'
const ADD_UPLOAD = 'uploads/ADD_UPLOAD'
const UPDATE_UPLOAD = 'uploads/UPDATE_UPLOAD'
const REMOVE_UPLOAD = 'uploads/REMOVE_UPLOAD'
const ADD_REJECTED = 'uploads/ADD_REJECTED'
const REMOVE_REJECTED = 'uploads/REMOVE_REJECTED'
const UPDATE_FILE_LINK = 'uploads/UPDATE_FILE_LINK'
const UPDATE_FILE_DELETE = 'uploads/UPDATE_FILE_DELETE'
const REMOVE_LONG_TERM_FILE = 'uploads/REMOVE_LONG_TERM_FILE'

// Reducer is pure: uploads are keyed by S3 key; the upload objects carry their ManagedUpload and callbacks
const handler = (state = initialState, action) => {
  const {payload} = action

  switch (action.type) {
    case RESET_STATE:
      return {
        ...initialState
      }

    case UPDATE:
      return {
        ...state,
        ...payload
      }

    case ADD_UPLOAD:
    case UPDATE_UPLOAD:
      return {
        ...state,
        uploads: {
          ...state.uploads,
          [payload.key]: payload
        }
      }

    case REMOVE_UPLOAD: {
      const uploads = {...state.uploads}
      delete uploads[payload.key]

      return {
        ...state,
        uploads
      }
    }

    case ADD_REJECTED:
      return {
        ...state,
        rejected: [...state.rejected, payload]
      }

    case REMOVE_REJECTED:
      return {
        ...state,
        rejected: state.rejected.filter(r => r.id !== payload)
      }

    case UPDATE_FILE_LINK:
      return {
        ...state,
        fileLink: {
          ...state.fileLink,
          ...payload
        }
      }

    case UPDATE_FILE_DELETE:
      return {
        ...state,
        fileDelete: {
          ...state.fileDelete,
          ...payload
        }
      }

    case REMOVE_LONG_TERM_FILE:
      return {
        ...state,
        longTermFiles: state.longTermFiles.filter(f => f.id !== payload)
      }

    default:
      return state
  }
}

// The ingest Lambda (SF-S3-File-Upload) renames any file whose base name has characters outside [a-zA-Z0-9_-] or
// trailing spaces, and that rename copy drops the object metadata (version, uploadedbycustomercontact). Applying the
// same rule here means the object lands with its final name and keeps its metadata.
export const safeFilename = name => {
  const dot = name.lastIndexOf('.')
  const base = dot > 0 ? name.slice(0, dot) : name
  const extension = dot > 0 ? name.slice(dot + 1).toLowerCase() : ''
  const safeBase = base.trim().replace(/[^a-zA-Z0-9_-]/g, '_')
  return extension ? `${safeBase}.${extension}` : safeBase
}

// Newest first (createdDate is the time the file was received / stored)
const newestFirst = files => [...(files || [])].sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate))

// Loads everything the Uploads tab shows (called when the tab is opened, and on refresh)
export const requestHubUploads = () => {
  return async (dispatch, getState) => {
    dispatch({
      type: UPDATE,
      payload: {
        loading: true,
        hasError: false,
        error: ''
      }
    })

    try {
      const {
        user
      } = getState().stores.auth

      const {
        selectedVersionId
      } = getState().stores.uploads

      const data = await getHubUploads(user)

      const {
        eligible,
        account,
        bucket,
        versions,
        recentUploads,
        longTermEnabled,
        longTermFiles
      } = data

      const versionList = versions || []

      // Keep the user's choice when it is still valid; pre-select when there is exactly one Version
      let nextSelectedVersionId = versionList.some(v => v.id === selectedVersionId) ? selectedVersionId : ''

      if (!nextSelectedVersionId && versionList.length === 1) {
        nextSelectedVersionId = versionList[0].id
      }

      dispatch({
        type: UPDATE,
        payload: {
          fetched: true,
          eligible: eligible === true,
          account: account || null,
          bucket: bucket || null,
          versions: versionList,
          selectedVersionId: nextSelectedVersionId,
          recentUploads: newestFirst(recentUploads),
          recentPage: 1,
          longTermEnabled: longTermEnabled === true,
          longTermFiles: newestFirst(longTermFiles),
          longTermPage: 1,
          longTermVersionFilter: longTermVersionOptions(longTermFiles || []).some(o => o.id === getState().stores.uploads.longTermVersionFilter)
            ? getState().stores.uploads.longTermVersionFilter
            : VERSION_FILTER_ALL
        }
      })
    }
    catch (e) {
      dispatch({
        type: UPDATE,
        payload: {
          error: e.message,
          hasError: true
        }
      })
    }

    dispatch({
      type: UPDATE,
      payload: {
        loading: false
      }
    })
  }
}

export const requestSelectVersion = versionId => {
  return dispatch => {
    dispatch({
      type: UPDATE,
      payload: {
        selectedVersionId: versionId || ''
      }
    })
  }
}

// Long-term files narrowed by the Version filter (distinct Versions come from the files themselves, not from the
// upload-target list, so deactivated Versions still show up)
export const filterLongTermFiles = (files, versionFilter) => {
  if (versionFilter === VERSION_FILTER_ALL) return files
  if (versionFilter === VERSION_FILTER_UNASSIGNED) return files.filter(f => !f.versionId)
  return files.filter(f => f.versionId === versionFilter)
}

export const longTermVersionOptions = files => {
  const seen = new Map()
  let hasUnassigned = false

  files.forEach(f => {
    if (!f.versionId) hasUnassigned = true
    else if (!seen.has(f.versionId)) seen.set(f.versionId, f.versionName || f.versionId)
  })

  const options = [...seen.entries()]
    .map(([id, name]) => ({id, name}))
    .sort((a, b) => a.name.localeCompare(b.name))

  if (hasUnassigned) options.push({id: VERSION_FILTER_UNASSIGNED, name: 'Unassigned'})

  return options
}

export const requestLongTermVersionFilter = versionFilter => {
  return dispatch => {
    dispatch({
      type: UPDATE,
      payload: {
        longTermVersionFilter: versionFilter || VERSION_FILTER_ALL,
        longTermPage: 1
      }
    })
  }
}

// list: RECENT | LONG_TERM
export const requestPage = (list, pageNumber) => {
  return (dispatch, getState) => {
    const {recentUploads, longTermFiles, longTermVersionFilter, recentPageSize, longTermPageSize} = getState().stores.uploads
    const files = list === LONG_TERM ? filterLongTermFiles(longTermFiles, longTermVersionFilter) : recentUploads
    const pageSize = list === LONG_TERM ? longTermPageSize : recentPageSize
    const numberOfPages = Math.max(Math.ceil(files.length / pageSize), 1)
    const next = Math.min(Math.max(pageNumber, 1), numberOfPages)

    dispatch({
      type: UPDATE,
      payload: list === LONG_TERM ? {longTermPage: next} : {recentPage: next}
    })
  }
}

// Rows per page for one list; back to the first page so the range stays valid
export const requestPageSize = (list, pageSize) => {
  return dispatch => {
    const size = PAGE_SIZE_OPTIONS.includes(pageSize) ? pageSize : DEFAULT_PAGE_SIZE

    dispatch({
      type: UPDATE,
      payload: list === LONG_TERM ? {longTermPageSize: size, longTermPage: 1} : {recentPageSize: size, recentPage: 1}
    })
  }
}

// Starts the next QUEUED upload when nothing is UPLOADING (one file at a time, 4 parts in parallel).
// Side effects live here, not in the reducer.
export const startNextIfCan = () => {
  return (dispatch, getState) => {
    const {uploads} = getState().stores.uploads
    const values = Object.values(uploads)

    if (values.some(u => u.status === UPLOADING)) {
      return
    }

    const next = values.find(u => u.status === QUEUED)

    if (!next) {
      return
    }

    next.startTime = Date.now()
    next.status = UPLOADING
    next.managedUpload.tags.push({
      'Key': 'StartTime',
      'Value': new Date().getTime().toString()
    })

    dispatch({
      type: UPDATE_UPLOAD,
      payload: next
    })

    next.managedUpload.on('httpUploadProgress', data => next.progress(data.loaded))

    next.managedUpload.send((error, data) => {
      if (error) {
        if (error.toString() !== 'RequestAbortedError: Request aborted by user') {
          console.error(error)
          return next.error(error)
        }
      }
      else {
        return next.complete()
      }
    })
  }
}

// Every file is uploaded against the Version selected in the picklist (required, no fallback)
export const requestHandleNewFiles = filesArray => {
  return (dispatch, getState) => {
    const {
      uploads,
      account,
      bucket,
      versions,
      selectedVersionId
    } = getState().stores.uploads

    const {
      contactId
    } = getState().stores.auth

    const version = versions.find(v => v.id === selectedVersionId)

    Array.from(filesArray).forEach(file => {
      const {size} = file
      const originalName = file.name
      const name = safeFilename(originalName)

      if (!version) {
        dispatch({
          type: ADD_REJECTED,
          payload: {
            id: `${name}-${Date.now()}`,
            name,
            reason: NO_VERSION
          }
        })
        return
      }

      const alreadyUploading = Object.values(uploads).some(u => u.name === name && u.status !== COMPLETE)

      if (alreadyUploading) {
        dispatch({
          type: ADD_REJECTED,
          payload: {
            id: `${name}-${Date.now()}`,
            name,
            reason: DUPLICATE
          }
        })
        return
      }

      const key = account.rootpath + name

      // Read by the S3 ingest Lambda: 'version' becomes the Version lookup on the S3 File record,
      // which drives the automatic order (and the copy to long-term storage when the Version has it).
      const metadata = [
        {name: 'uploadmethod', value: 'S3 Uploader'},
        {name: 'version', value: version.id},
        {name: 'uploadedbycustomercontact', value: contactId}
      ]

      const tags = [
        {name: 'UploadMethod', value: 'S3 Uploader'},
        {name: 'VersionId', value: version.id},
        {name: 'UploadedByCustomerContact', value: contactId}
      ]

      let upload = {
        name,
        originalName: originalName !== name ? originalName : null,
        payload: file,
        key,
        bucket,
        versionId: version.id,
        versionName: version.name,
        status: QUEUED,
        uploadedBytes: 0,
        totalBytes: size,
        uploadedBytesFriendly: bytesConvertor(0),
        totalBytesFriendly: bytesConvertor(size),
        percentage: 0,
        metadata,
        tags
      }

      upload.managedUpload = registerUpload(upload)

      upload.abortUpload = () => {
        if (upload.status === UPLOADING || upload.status === QUEUED) {
          if (upload.status === UPLOADING) {
            upload.managedUpload.abort()
          }

          upload.status = CANCELLED
          upload.percentage = 100

          dispatch({
            type: REMOVE_UPLOAD,
            payload: upload
          })

          dispatch(startNextIfCan())
        }
      }

      upload.complete = () => {
        upload.status = COMPLETE
        upload.percentage = 100
        upload.uploadTime = timeConvertor(Date.now() - upload.startTime)

        dispatch({
          type: UPDATE_UPLOAD,
          payload: upload
        })

        dispatch(startNextIfCan())
      }

      upload.error = error => {
        upload.status = FAILED
        upload.errorMessage = `Upload failed: ${error.name || error.message || error}`
        upload.percentage = 100

        dispatch({
          type: UPDATE_UPLOAD,
          payload: upload
        })

        dispatch(startNextIfCan())
      }

      upload.progress = uploadedBytes => {
        upload.uploadedBytes = uploadedBytes
        upload.percentage = parseFloat((uploadedBytes / upload.totalBytes * 100).toFixed(1))
        upload.uploadedBytesFriendly = bytesConvertor(uploadedBytes)

        // remaining time estimate
        upload.timeLeftForUpload = (upload.totalBytes - uploadedBytes) * ((Date.now() - upload.startTime) / uploadedBytes)
        upload.timeLeftForUploadFriendly = timeConvertor(upload.timeLeftForUpload)

        dispatch({
          type: UPDATE_UPLOAD,
          payload: upload
        })
      }

      dispatch({
        type: ADD_UPLOAD,
        payload: upload
      })
    })

    dispatch(startNextIfCan())
  }
}

export const requestRemoveRejected = id => {
  return dispatch => {
    dispatch({
      type: REMOVE_REJECTED,
      payload: id
    })
  }
}

export const requestClearFinishedUploads = () => {
  return (dispatch, getState) => {
    const {uploads} = getState().stores.uploads

    Object.values(uploads).forEach(u => {
      if (u.status === COMPLETE || u.status === FAILED || u.status === CANCELLED) {
        dispatch({
          type: REMOVE_UPLOAD,
          payload: u
        })
      }
    })
  }
}

export const requestFileLink = (file, linkType, mode = LINK_MODE_DOWNLOAD) => {
  return async (dispatch, getState) => {
    dispatch({
      type: UPDATE_FILE_LINK,
      payload: {
        show: true,
        loading: true,
        mode,
        file,
        fileId: file.id,
        fileName: file.name,
        linkType,
        link: null,
        hasError: false,
        error: ''
      }
    })

    try {
      const {
        user
      } = getState().stores.auth

      const data = await getHubFileLink(user, file.id, linkType)

      if (!data.isAvailable || !data.link) {
        throw new Error(`The ${linkType} file is not available right now. Please try again later or contact Aberdeen.`)
      }

      dispatch({
        type: UPDATE_FILE_LINK,
        payload: {
          link: data.link
        }
      })
    }
    catch (e) {
      dispatch({
        type: UPDATE_FILE_LINK,
        payload: {
          hasError: true,
          error: e.message
        }
      })
    }

    dispatch({
      type: UPDATE_FILE_LINK,
      payload: {
        loading: false
      }
    })
  }
}

export const requestCloseFileLink = () => {
  return dispatch => {
    dispatch({
      type: UPDATE_FILE_LINK,
      payload: {
        show: false,
        loading: false,
        link: null,
        hasError: false,
        error: ''
      }
    })
  }
}

export const requestOpenFileDelete = file => {
  return dispatch => {
    dispatch({
      type: UPDATE_FILE_DELETE,
      payload: {
        show: true,
        loading: false,
        file,
        hasError: false,
        error: ''
      }
    })
  }
}

export const requestCloseFileDelete = () => {
  return dispatch => {
    dispatch({
      type: UPDATE_FILE_DELETE,
      payload: {
        show: false,
        loading: false,
        file: null,
        hasError: false,
        error: ''
      }
    })
  }
}

// Hard delete of a long-term file (and its proxy). Apex enforces that only the uploader may delete; the portal only
// shows the control when canDelete is true. Errors arrive as the Salesforce message text.
export const requestConfirmFileDelete = () => {
  return async (dispatch, getState) => {
    const {fileDelete} = getState().stores.uploads
    const file = fileDelete.file

    if (!file) return

    dispatch({
      type: UPDATE_FILE_DELETE,
      payload: {
        loading: true,
        hasError: false,
        error: ''
      }
    })

    try {
      const {user} = getState().stores.auth
      const data = await deleteHubFile(user, file.id)

      if (!data || data.deleted !== true) {
        throw new Error('The file could not be deleted. Please try again later.')
      }

      dispatch({type: REMOVE_LONG_TERM_FILE, payload: file.id})
      dispatch(requestCloseFileDelete())
    }
    catch (e) {
      // 409 FILE_ALREADY_DELETED: it is gone already, so just drop the row
      if (/already been deleted/i.test(e.message || '')) {
        dispatch({type: REMOVE_LONG_TERM_FILE, payload: file.id})
        dispatch(requestCloseFileDelete())
        return
      }

      dispatch({
        type: UPDATE_FILE_DELETE,
        payload: {
          loading: false,
          hasError: true,
          error: e.message
        }
      })
    }
  }
}

export default handler
